import { useMemo, useState } from "react";
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsNumberLiteral,
  parseAsString,
  useQueryStates,
} from "nuqs";
import { IconAlertCircle, IconUpload } from "@tabler/icons-react";
import { Selectable } from "../UI/Selectable";
// @ts-expect-error pure js
import useTransactions from "../../hooks/use-transactions";
import TransactionSplitForm from "./TransactionSplitForm";
import BankStatementImportDialog from "./BankStatementImportDialog";
import Button from "../UI/Button";
// @ts-expect-error pure js
import useFinanceSplitTemplates from "../../hooks/use-finance-split-templates";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import useDebouncedValue from "../../hooks/use-debounced-value";
// @ts-expect-error pure js
import useMediaQuery from "../../hooks/use-media-query";
import ButtonCard from "../UI/ButtonCard";
// @ts-expect-error pure js
import { currencyFormatter, dateConvert } from "../../api/Functions";
import { Table } from "../UI/Table";
import Text from "../UI/Text";
import Status from "../UI/Status";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import AllocationChip from "./AllocationChip";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection, {
  FilterRangeSection,
} from "../UI/FilterBar/FilterSection";
import type { Bank } from "../../models/bank";
import type { Balance } from "../../models/balance";
import type { Transaction } from "../../models/transaction";
import styles from "./TransactionList.module.css";

const STATUS_OPTIONS = [
  { value: "affected", label: "Affectées" },
  { value: "unaffected", label: "Non affectées" },
];

const PER_PAGE_VALUES = [10, 50, 100, 500] as const;

interface PerPageOption {
  value: (typeof PER_PAGE_VALUES)[number];
  label: string;
}

const PER_PAGE_OPTIONS: PerPageOption[] = PER_PAGE_VALUES.map((value) => ({
  value,
  label: String(value),
}));

function useTransactionListFilters() {
  return useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      perPage: parseAsNumberLiteral(PER_PAGE_VALUES).withDefault(100),
      search: parseAsString.withDefault(""),
      status: parseAsArrayOf(parseAsString).withDefault([]),
      accounts: parseAsArrayOf(parseAsString).withDefault([]),
      bailleurs: parseAsArrayOf(parseAsString).withDefault([]),
      montantMin: parseAsString.withDefault(""),
      montantMax: parseAsString.withDefault(""),
      dateMin: parseAsString.withDefault(""),
      dateMax: parseAsString.withDefault(""),
    },
    {
      urlKeys: {
        perPage: "per_page",
        status: "statut",
        accounts: "account",
        bailleurs: "bailleur",
        montantMin: "montant_min",
        montantMax: "montant_max",
        dateMin: "date_min",
        dateMax: "date_max",
      },
      // Les onglets (Tabs) lisent l'URL via react-router : une mise à jour
      // « shallow » leur serait invisible et ils écraseraient ces paramètres
      // au prochain changement d'onglet.
      shallow: false,
    },
  );
}

interface TransactionListProps {
  banks: Bank[];
}

interface SplitFormResult {
  balances: Balance[];
  update_template?: boolean;
  template?: string;
  save_keyword?: boolean;
}

function isParentTransaction(transaction: Transaction): boolean {
  return !transaction.id_transaction;
}

function isSimpleFullAllocation(transaction: Transaction): boolean {
  return (
    transaction.balances?.length === 1 &&
    transaction.balances[0].montant === transaction.montant
  );
}

function findBankLabel(
  banks: Bank[],
  uniqueAccountId: string | undefined,
): string | null {
  const bank = banks.find((b) => b.unique_id === uniqueAccountId);
  if (!bank) return null;
  return bank.nom || bank.bank_metadata?.name || null;
}

export default function TransactionList({ banks }: TransactionListProps) {
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [shouldPrefillSplit, setShouldPrefillSplit] = useState(false);
  const [filters, setFilters] = useTransactionListFilters();
  const {
    page,
    perPage,
    search,
    status,
    accounts,
    bailleurs,
    montantMin,
    montantMax,
    dateMin,
    dateMax,
  } = filters;
  const debouncedSearch = useDebouncedValue(search);
  const debouncedMontantMin = useDebouncedValue(montantMin);
  const debouncedMontantMax = useDebouncedValue(montantMax);
  const debouncedDateMin = useDebouncedValue(dateMin);
  const debouncedDateMax = useDebouncedValue(dateMax);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 1100px)");
  const { profileBailleurs } = useProfileBailleurs();

  const affectedParam = useMemo(() => {
    if (status.length !== 1) return undefined;
    return status[0] === "affected" ? "true" : "false";
  }, [status]);

  const accountIds = useMemo(() => {
    const fromBailleur = banks
      .filter((b) => bailleurs.includes(String(b.id_profile_nom)))
      .map((b) => b.unique_id);
    return Array.from(new Set([...accounts, ...fromBailleur]));
  }, [banks, accounts, bailleurs]);

  const {
    isLoading,
    transactions,
    deleteMultipleTransactions,
    createTransactionBalances,
    refreshTransactions,
  } = useTransactions({
    params: {
      page,
      per_page: perPage,
      search: debouncedSearch || undefined,
      account: accountIds,
      montant_min: debouncedMontantMin || undefined,
      montant_max: debouncedMontantMax || undefined,
      affected: affectedParam,
      date_min: debouncedDateMin || undefined,
      date_max: debouncedDateMax || undefined,
    },
  });
  const { splitTemplates, updateFinanceSplitTemplate } =
    useFinanceSplitTemplates();
  const [isSplitOpen, setIsSplitOpen] = useState(false);

  async function splitFormSubmitHandler(
    newTransaction: SplitFormResult,
    similarTransactionIds: (string | number)[],
  ) {
    if (!transaction) return;
    if (newTransaction.update_template) {
      await updateFinanceSplitTemplate(
        parseInt(newTransaction.template ?? "", 10),
        {
          splits: newTransaction.balances,
        },
      );
    }
    await createTransactionBalances(transaction.id, newTransaction.balances, {
      similarTransactionIds,
      saveKeyword: newTransaction.save_keyword,
    });
    setIsSplitOpen(false);
    setTransaction(null);
    setShouldPrefillSplit(false);
  }

  const bankOptions = banks.map((b) => ({
    value: b.unique_id,
    label: b.nom || b.bank_metadata?.name || "Banque",
  }));
  const bailleurOptions = profileBailleurs.map(
    (p: { id: number; nom_profile: string }) => ({
      value: String(p.id),
      label: p.nom_profile,
    }),
  );

  const activeFiltersCount =
    (status.length > 0 ? 1 : 0) +
    (accounts.length > 0 ? 1 : 0) +
    (bailleurs.length > 0 ? 1 : 0) +
    (montantMin || montantMax ? 1 : 0) +
    (dateMin || dateMax ? 1 : 0);

  function clearFilters() {
    setFilters({
      page: null,
      status: null,
      accounts: null,
      bailleurs: null,
      montantMin: null,
      montantMax: null,
      dateMin: null,
      dateMax: null,
    });
  }

  function affectationClickHandler(clickedTransaction: Transaction) {
    setTransaction(clickedTransaction);
    setShouldPrefillSplit(true);
    setIsSplitOpen(true);
  }

  return (
    <>
      <TransactionSplitForm
        open={isSplitOpen}
        onOpenChange={(open: boolean) => {
          setIsSplitOpen(open);
          if (!open) {
            setTransaction(null);
            setShouldPrefillSplit(false);
          }
        }}
        transaction={transaction}
        shouldPrefillSplit={shouldPrefillSplit}
        splitTemplates={splitTemplates}
        banks={banks}
        onSubmit={splitFormSubmitHandler}
      />
      <BankStatementImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        banks={banks}
        onImported={refreshTransactions}
      />
      <>
        <FilterBar
          searchValue={search}
          onSearchChange={(value) => setFilters({ search: value, page: null })}
          searchPlaceholder="Rechercher une transaction"
          activeFiltersCount={activeFiltersCount}
          onClearFilters={clearFilters}
          leftActions={
            <Button
              icon={IconUpload}
              variant="secondary"
              onClick={() => setIsImportOpen(true)}
            >
              Importer un relevé
            </Button>
          }
        >
          <FilterSection
            title="Statut"
            options={STATUS_OPTIONS}
            values={status}
            onChange={(values) => setFilters({ status: values, page: null })}
            defaultOpen
          />
          <FilterSection
            title="Compte"
            options={bankOptions}
            values={accounts}
            onChange={(values) => setFilters({ accounts: values, page: null })}
          />
          <FilterSection
            title="Bailleur"
            options={bailleurOptions}
            values={bailleurs}
            onChange={(values) => setFilters({ bailleurs: values, page: null })}
          />
          <FilterRangeSection
            title="Montant"
            unit="€"
            minValue={montantMin}
            maxValue={montantMax}
            onMinChange={(value) =>
              setFilters({ montantMin: value, page: null })
            }
            onMaxChange={(value) =>
              setFilters({ montantMax: value, page: null })
            }
          />
          <FilterRangeSection
            title="Date"
            minLabel="Du"
            maxLabel="Au"
            inputType="date"
            minValue={dateMin}
            maxValue={dateMax}
            onMinChange={(value) => setFilters({ dateMin: value, page: null })}
            onMaxChange={(value) => setFilters({ dateMax: value, page: null })}
          />
        </FilterBar>
        {isLoading ? (
          <IsLoading />
        ) : (
          <Selectable items={transactions}>
            {isSmallScreen ? (
              <TransactionListSmallScreen
                transactions={transactions}
                onAffect={affectationClickHandler}
              />
            ) : (
              <TransactionListBigScreen
                transactions={transactions}
                banks={banks}
                onAffect={affectationClickHandler}
              />
            )}
            <div className={styles.tableFooter}>
              <div className={styles.footerLeft}>
                {!isSmallScreen && (
                  <Selectable.ActionButton
                    disabled={isBatchLoading}
                    isLoading={isBatchLoading}
                    resetOnClick
                    onClick={async (selectedItems: (string | number)[]) => {
                      try {
                        setIsBatchLoading(true);
                        await deleteMultipleTransactions(selectedItems);
                      } catch (e) {
                        console.error(e);
                      }
                      setIsBatchLoading(false);
                    }}
                  >
                    Supprimer
                  </Selectable.ActionButton>
                )}
              </div>
              <div className={styles.footerRight}>
                <ReactSelectStandalone
                  className={styles.perPageSelect}
                  name="perPage"
                  onChange={(option: PerPageOption | null) =>
                    setFilters({ perPage: option?.value ?? null, page: null })
                  }
                  value={PER_PAGE_OPTIONS.find(
                    (option) => option.value === perPage,
                  )}
                  options={PER_PAGE_OPTIONS}
                />
                <Button
                  variant="secondary"
                  onClick={() => setFilters({ page: Math.max(1, page - 1) })}
                  disabled={page <= 1}
                >
                  Précédent
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setFilters({ page: page + 1 })}
                >
                  Suivant
                </Button>
              </div>
            </div>
          </Selectable>
        )}
      </>
    </>
  );
}

interface TransactionListBigScreenProps {
  transactions: Transaction[];
  banks: Bank[];
  onAffect: (transaction: Transaction) => void;
}

function TransactionListBigScreen({
  transactions,
  banks,
  onAffect,
}: TransactionListBigScreenProps) {
  const parents = transactions.filter(isParentTransaction);

  return (
    <Table variant="card" className={styles.table}>
      <Table.Header>
        <Table.Column>
          <Selectable.Checkbox selectAll />
        </Table.Column>
        <Table.Column>Date</Table.Column>
        <Table.Column>Description &amp; affectations</Table.Column>
        <Table.Column>Compte</Table.Column>
        <Table.Column align={"right"}>Montant</Table.Column>
      </Table.Header>
      <Table.Body>
        {parents.map((transaction, i) => {
          const allocations = transaction.balances ?? [];
          const hideChipAmount = isSimpleFullAllocation(transaction);
          const bankLabel = findBankLabel(banks, transaction.unique_account_id);
          const isPositive = transaction.montant > 0;

          return (
            <Table.Row
              key={transaction.id}
              className={styles.row}
              onClick={() => onAffect(transaction)}
            >
              <Table.Cell
                onClick={(e) => e.stopPropagation()}
                className={styles.checkboxCell}
              >
                <Selectable.Checkbox value={transaction.id} index={i} />
              </Table.Cell>
              <Table.Cell className={styles.dateCell}>
                <Text color="muted">
                  {dateConvert(transaction.date_transac)}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <div className={styles.descriptionStack}>
                  <Text variant="base" weight="medium">
                    {transaction.description}
                  </Text>
                  <div className={styles.chipsRow}>
                    {allocations.length > 0 ? (
                      allocations.map((balance) => (
                        <AllocationChip
                          key={balance.id}
                          balance={balance}
                          showAmount={!hideChipAmount}
                        />
                      ))
                    ) : (
                      <Status
                        as="span"
                        color="neutral"
                        text={
                          <span className={styles.unaffectedBody}>
                            <IconAlertCircle size={14} stroke={1.5} />
                            Non affectée
                          </span>
                        }
                      />
                    )}
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className={styles.bankCell}>
                <Text color="muted" className={styles.bankLabel}>
                  {bankLabel ?? "—"}
                </Text>
              </Table.Cell>
              <Table.Cell align={"right"} className={styles.amountCell}>
                <Text
                  as="span"
                  variant="base"
                  weight="bold"
                  color={isPositive ? "success" : "neutral"}
                >
                  {currencyFormatter(transaction.montant, { withSign: true })}
                </Text>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}

interface TransactionListSmallScreenProps {
  transactions: Transaction[];
  onAffect: (transaction: Transaction) => void;
}

function TransactionListSmallScreen({
  transactions,
  onAffect,
}: TransactionListSmallScreenProps) {
  const parents = transactions.filter(isParentTransaction);

  return (
    <div className={styles.mobileList}>
      {parents.map((transaction) => {
        const allocations = transaction.balances ?? [];
        const hideChipAmount = isSimpleFullAllocation(transaction);
        const isPositive = transaction.montant > 0;

        return (
          <ButtonCard
            key={transaction.id}
            onClick={() => onAffect(transaction)}
          >
            <div className={styles.mobileHeader}>
              <Text variant="deci" color="muted" className={styles.mobileItem}>
                {dateConvert(transaction.date_transac)}
              </Text>
              <Text
                as="span"
                variant="base"
                weight="bold"
                color={isPositive ? "success" : "neutral"}
              >
                {isPositive ? "+" : ""}
                {currencyFormatter(transaction.montant)}
              </Text>
            </div>
            <Text variant="base" weight="medium" className={styles.mobileItem}>
              {transaction.description}
            </Text>
            <div className={styles.chipsRow}>
              {allocations.length > 0 ? (
                allocations.map((balance) => (
                  <AllocationChip
                    key={balance.id}
                    balance={balance}
                    showAmount={!hideChipAmount}
                  />
                ))
              ) : (
                <Status
                  as="span"
                  color="neutral"
                  variant="deci"
                  text={
                    <span className={styles.unaffectedBody}>
                      <IconAlertCircle size={14} stroke={1.5} />
                      Non affectée
                    </span>
                  }
                />
              )}
            </div>
          </ButtonCard>
        );
      })}
    </div>
  );
}
